import type { DifferentiationQuestion, Situation } from "../types.js";

/**
 * Las siete situaciones del alfa 1-6-9, CALIBRADAS.
 *
 * Los pesos los puso el autor el 17 de agosto de 2026, como exige el
 * `DEFINITION_OF_DONE`:
 *
 *   «No LLM or opaque model decides the classification. An LLM may assist with
 *    copy drafting later, but never with scoring…»
 *
 * Redactar estas opciones fue copy y estuvo asistido. Los números no.
 *
 * ── EL CRITERIO CON EL QUE SE PONDERÓ ──────────────────────────────────────
 *
 * No se premia la CONDUCTA típica: se pondera la MOTIVACIÓN que la organiza.
 * El 1 revisa por corrección interna; el 6, por incertidumbre y prevención de
 * riesgo; el 9 pospone o cede para conservar estabilidad y evitar fricción. Es
 * la distinción que ya hacen los Bloques E 1↔6 y 9↔6. Dos opciones pueden
 * describir el mismo acto —revisar algo dos veces— y apuntar a tipos distintos;
 * lo que las separa es para qué.
 *
 * La escala:
 *   +2  evidencia motivacional directa
 *   +1  indicio compatible, pero compartido o ambiguo
 *   -2  contraevidencia clara
 *    0  no informa sobre 1, 6 ni 9
 *
 * **±3 no se usa**, aunque el motor lo permita: ningún reactivo aislado es
 * prácticamente diagnóstico. Que el techo exista y no se toque es deliberado.
 *
 * Y esto da validez de CONTENIDO, no validación psicométrica. Lo que lo hará
 * defendible es el piloto, que sigue pendiente.
 *
 * PROCEDENCIA. Ninguna opción está inventada. Todas salen de
 * `docs/canonical/Plantilla_maestra_nueve_eneatipos.docx` (y de la v1.1 para
 * los tipos 1 y 9), con este mapeo:
 *
 *   Bloque E · «Conducta compartida»               → el enunciado (`prompt`)
 *   Bloque E · «Señal que favorece cada hipótesis» → las dos opciones opuestas
 *   Bloque D · «Señales favorables»                → `evidenceTags`
 *   Bloque D · «Señales contrarias»                → `counterevidenceTags`
 *   Bloque D · «Qué la debilitaría claramente»     → la opción que degrada a `open`
 *   Bloque D · «Posibles falsos positivos»         → opción de otro patrón
 *
 * POR QUÉ EL ALFA ES 1-6-9 Y NO 1-9: comprobado par por par, ni el 1 lista al
 * 9 ni el 9 al 1 como confundibles — la v1.1 los eligió por opuestos, como
 * prueba estructural de la plantilla. El 6 es el único tipo que el material
 * cruza con ambos, y sus dos Bloques E ya están escritos. Detalle en
 * `docs/PLAN-inner-map.md` §7-bis.
 *
 * ESTRUCTURA: 3 de señal base (una por tipo) + 2 de desempate (una por par
 * real) + 2 de contraevidencia. Las dos últimas son las que hacen `open`
 * alcanzable de forma honesta y no por accidente.
 *
 * Todo en inglés: la app sale en inglés.
 */

const V = "draft-0.1";

/** Marca explícita: sin pesos, esta opción no puntúa nada. La pone el autor. */
/**
 * Sin señal sobre ninguno de los tres. **No es «aún sin calibrar»**: es la
 * respuesta que NO debe puntuar a nadie — S2_b, S5_d y S6_c.
 *
 * Se exporta porque en tiempo de ejecución un cero deliberado y un cero por
 * olvido son el mismo `{}`, y el banco de calibración necesita distinguirlos
 * para no avisar de las tres que están bien. La comparación es por IDENTIDAD
 * de objeto: usar esta constante es la declaración de intención.
 */
export const SIN_SENAL = {} as const;

export const DRAFT_SITUATIONS: Situation[] = [
  // ── S1 · señal base del patrón 1 ────────────────────────────────────────
  // Bloque D del 1: «Autocrítica verbalizada frecuente; corrección espontánea
  // de otros sin que se pida; dificultad para dar un trabajo por terminado».
  {
    id: "S1",
    prompt: "You finished something and nobody has asked you about it. What actually happens next?",
    context: "work or a personal project",
    purpose: "Base signal for pattern 1: the standard that keeps running after the task is done.",
    required: true,
    version: V,
    options: [
      {
        id: "S1_a",
        text: "I go back over it looking for what's still not right.",
        signals: ["revisión repetida", "dificultad para dar por terminado"],
        evidenceTags: ["1:no-cierra-tareas"],
        counterevidenceTags: [],
        // Estándar interno que sigue activo aunque nadie supervise: la corrección
        // vale por sí misma.
        patternWeights: { "1": 2 },
        confidence: "medium",
        sourceStatus: "fuente",
        notes: "Bloque D del 1, señales favorables."
      },
      {
        id: "S1_b",
        text: "I check whether anything about it could come back and cause a problem.",
        signals: ["anticipación de riesgo"],
        evidenceTags: ["6:anticipa-peligro"],
        counterevidenceTags: [],
        // Revisa para anticipar problemas, no para perfeccionar. Seguridad, no
        // estándar.
        patternWeights: { "6": 2 },
        confidence: "medium",
        sourceStatus: "fuente",
        notes: "Distractor del 6: misma conducta de revisar, motivo distinto."
      },
      {
        id: "S1_c",
        text: "I hand it over and move on without rereading it.",
        signals: ["cierre sin revisión"],
        evidenceTags: [],
        counterevidenceTags: ["1:tolera-error-propio"],
        // Entregar sin releer contradice la vigilancia del 1. No resta al 6: un 6
        // puede no revisar lo que no juzga arriesgado.
        patternWeights: { "1": -2 },
        confidence: "medium",
        sourceStatus: "fuente",
        notes: "Bloque D del 1, señales contrarias: contraevidencia."
      }
    ]
  },

  // ── S2 · señal base del patrón 9 ────────────────────────────────────────
  // Bloque D del 9: «Decir "sí" sin decisión interna clara; aplazamiento
  // repetido de tareas prioritarias».
  {
    id: "S2",
    prompt: "Someone proposes a plan you don't especially want. What do you say?",
    context: "friends, partner or family",
    purpose: "Base signal for pattern 9: agreeing before deciding.",
    required: true,
    version: V,
    options: [
      {
        id: "S2_a",
        text: "I say yes, and only later notice I didn't want to.",
        signals: ["decir sí sin decisión interna"],
        evidenceTags: ["9:cede-sin-decidir"],
        counterevidenceTags: [],
        // Decir que sí antes de registrar el propio deseo: autoabandono. Evidencia
        // directa.
        patternWeights: { "9": 2 },
        confidence: "medium",
        sourceStatus: "fuente",
        notes: "Bloque D del 9, señales favorables."
      },
      {
        id: "S2_b",
        text: "I help make it work, and I'd rather they didn't have to ask twice.",
        signals: ["ayuda no solicitada"],
        evidenceTags: [],
        counterevidenceTags: [],
        // Ayudar para que no lo pidan dos veces puede venir del 2, del 3 o de simple
        // educación. No identifica al 9.
        patternWeights: SIN_SENAL,
        confidence: "low",
        sourceStatus: "hipotesis",
        notes: "Falso positivo nombrado en el Bloque D del 9: ceder por estrategia " +
               "relacional (tipo 2), que NO está en el alfa. Sin peso hacia ningún " +
               "patrón activo — existe para que el 9 no se lleve esta respuesta."
      },
      {
        id: "S2_c",
        text: "I say what I'd prefer, even if it makes things awkward.",
        signals: ["nombra preferencia propia"],
        evidenceTags: [],
        counterevidenceTags: ["9:nombra-preferencia"],
        // Expresar la preferencia aunque incomode contradice el borrado de la
        // posición propia. No -3: un 9 sano lo hace.
        patternWeights: { "9": -2 },
        confidence: "medium",
        sourceStatus: "fuente",
        notes: "Bloque D del 9, señales contrarias."
      }
    ]
  },

  // ── S3 · señal base del patrón 6 ────────────────────────────────────────
  // Bloque D del 6: «Uso frecuente de "¿y si...?" y "no sé"; consulta repetida
  // a varias personas antes de decidir lo mismo».
  {
    id: "S3",
    prompt: "A decision matters and the information is incomplete. What do you do first?",
    context: "any",
    purpose: "Base signal for pattern 6: seeking certainty or backing before deciding.",
    required: true,
    version: V,
    options: [
      {
        id: "S3_a",
        text: "I ask a few people I trust — sometimes the same question more than once.",
        signals: ["consulta repetida", "busca apoyo externo"],
        evidenceTags: ["6:consulta-repetida"],
        counterevidenceTags: [],
        // Triangular y repetir la pregunta: buscar respaldo porque el propio juicio
        // no basta.
        patternWeights: { "6": 2 },
        confidence: "medium",
        sourceStatus: "fuente",
        notes: "Bloque D del 6, señales favorables."
      },
      {
        id: "S3_b",
        text: "I decide with what I have and accept I might be wrong.",
        signals: ["decide con información incompleta"],
        evidenceTags: [],
        counterevidenceTags: ["6:decide-sin-validar"],
        // Decidir con información incompleta y aceptar el error contradice la
        // necesidad de reducir incertidumbre.
        patternWeights: { "6": -2 },
        confidence: "medium",
        sourceStatus: "fuente",
        notes: "Bloque D del 6, señales contrarias."
      },
      {
        id: "S3_c",
        text: "I put it off and it stops feeling urgent on its own.",
        signals: ["aplazamiento"],
        evidenceTags: ["9:aplaza"],
        counterevidenceTags: [],
        // Posponer puede ser duda (6) o inercia (9). Sin el motivo es evidencia
        // débil y compartida: +1 a cada uno. S5 la desempata.
        patternWeights: { "6": 1, "9": 1 },
        confidence: "medium",
        sourceStatus: "fuente",
        notes: "Conducta compartida 9↔6; aquí sin el motivo, que S5 desempata."
      }
    ]
  },

  // ── S4 · desempate 1↔6 ──────────────────────────────────────────────────
  // Bloque E del 1 frente al 6. Conducta compartida: «Ambos anticipan
  // problemas y revisan detalles antes de actuar». Señal: «Foco en "¿está bien
  // hecho?" favorece 1; foco en "¿es seguro?" favorece 6».
  {
    id: "S4",
    prompt: "You're going over something carefully before it goes out. What are you actually checking for?",
    context: "before acting or delivering",
    purpose: "Discriminates 1 from 6 on the SAME behaviour: correctness vs safety.",
    required: true,
    version: V,
    options: [
      {
        id: "S4_a",
        text: "Whether it's done properly — it should be right whether or not anyone notices.",
        signals: ["estándar interno de corrección"],
        evidenceTags: ["1:esta-bien-hecho"],
        counterevidenceTags: [],
        // «Done properly» y «whether or not anyone notices» aíslan el estándar
        // interiorizado frente a la aprobación.
        patternWeights: { "1": 2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque E 1↔6, señal que favorece 1."
      },
      {
        id: "S4_b",
        text: "Whether something could go wrong that I haven't thought of.",
        signals: ["reducción de incertidumbre"],
        evidenceTags: ["6:es-seguro"],
        counterevidenceTags: [],
        // Atención dirigida a amenazas no previstas: pensamiento prospectivo.
        patternWeights: { "6": 2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque E 1↔6, señal que favorece 6."
      },
      {
        id: "S4_c",
        text: "I don't really go over things beforehand.",
        signals: [],
        evidenceTags: [],
        counterevidenceTags: ["1:tolera-error-propio", "6:decide-sin-validar"],
        // No revisar contradice a los dos. NO demuestra 9: también es impulsividad,
        // confianza o desinterés.
        patternWeights: { "1": -2, "6": -2 },
        confidence: "medium",
        sourceStatus: "inferencia",
        notes: "Contraevidencia para los dos lados del par."
      }
    ]
  },

  // ── S5 · desempate 9↔6 ──────────────────────────────────────────────────
  // Bloque E del 9 frente al 6. Conducta compartida: «Ambos dudan y postergan
  // decisiones». Señal: «Postergar porque "da igual la opción" favorece 9;
  // postergar buscando información sobre el riesgo favorece 6».
  {
    id: "S5",
    prompt: "You've been putting off a decision for a while. If you're honest, why?",
    context: "a decision that is genuinely yours to make",
    purpose: "Discriminates 9 from 6 on the SAME behaviour: indifference vs risk.",
    required: true,
    version: V,
    options: [
      {
        id: "S5_a",
        text: "Honestly, either option seems fine — it doesn't feel worth the fuss.",
        signals: ["da igual la opción", "evitar la tensión"],
        evidenceTags: ["9:da-igual"],
        counterevidenceTags: [],
        // Minimizar la diferencia y no movilizar energía: «no vale la pena».
        patternWeights: { "9": 2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque E 9↔6, señal que favorece 9."
      },
      {
        id: "S5_b",
        text: "I keep looking for something that would tell me which one is the safer bet.",
        signals: ["busca información sobre el riesgo"],
        evidenceTags: ["6:busca-certeza"],
        counterevidenceTags: [],
        // Seguir buscando el dato que indique la opción segura: intolerancia a la
        // incertidumbre.
        patternWeights: { "6": 2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque E 9↔6, señal que favorece 6."
      },
      {
        id: "S5_c",
        text: "Because whichever I pick, I'll be annoyed it isn't the right one.",
        signals: ["estándar de corrección aplicado a la decisión"],
        evidenceTags: ["1:esta-bien-hecho"],
        counterevidenceTags: [],
        // Compatible con el 1, pero ambigua: también es maximización, o ansiedad del
        // 6. Sólo +1, y sigue marcada como [HIPÓTESIS].
        patternWeights: { "1": 1 },
        confidence: "low",
        sourceStatus: "hipotesis",
        notes: "Tercera vía del 1 sobre la misma conducta. [HIPÓTESIS]: el canon no " +
               "trata el par 1↔9 y aquí el 1 entra como tercero. Marcar en el piloto."
      },
      {
        id: "S5_d",
        text: "I'm not sure — none of these feels clearly true.",
        signals: [],
        evidenceTags: [],
        counterevidenceTags: [],
        // Salida neutra. No informa sobre 1, 6 ni 9.
        patternWeights: SIN_SENAL,
        confidence: "low",
        sourceStatus: "inferencia",
        notes: "Salida neutra. La añadí porque la guarda cazó que S5 era la única " +
               "situación donde las tres opciones alimentaban alguna hipótesis: sin " +
               "esto, la elección forzada obliga a puntuar a alguien aunque la " +
               "persona no se reconozca en ninguna. Mitigación ipsativa (decisión 0.3).\n" +
               "TEXTO CORREGIDO en la calibración: decía «I hadn't really noticed I " +
               "was putting it off», que NO es neutral — no darse cuenta de la propia " +
               "inercia es descripción del 9, así que la salida de escape puntuaba a " +
               "quien la usara por no reconocerse en nada. Ahora dice que ninguna " +
               "encaja, que es lo único que no informa sobre 1, 6 ni 9."
      }
    ]
  },

  // ── S6 · contraevidencia contra 6 y contra 1 ────────────────────────────
  // Bloque D: «qué la debilitaría claramente» del 6 y del 1.
  {
    id: "S6",
    prompt: "Think of the last few things you decided quickly. How did that go?",
    context: "recent, real decisions",
    purpose: "Reaches `open` honestly: makes counterevidence available, not accidental.",
    required: true,
    version: V,
    options: [
      {
        id: "S6_a",
        text: "Fine — I decide fast and don't need anyone to confirm it.",
        signals: ["decisiones rápidas sin validación"],
        evidenceTags: [],
        counterevidenceTags: ["6:decide-sin-validar"],
        // Decidir rápido sin necesitar confirmación es contraevidencia clara. No
        // suma al 1: también describe al 3, al 7 o al 8, sin el motivo de
        // corrección.
        patternWeights: { "6": -2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque D del 6: «qué la debilitaría claramente»."
      },
      {
        id: "S6_b",
        text: "I left things unfinished or imperfect and it genuinely didn't bother me.",
        signals: ["deja errores sin corregir sin malestar"],
        evidenceTags: [],
        counterevidenceTags: ["1:tolera-error-propio"],
        // Dejar algo imperfecto y que dé igual de verdad contradice el estándar
        // correctivo. No suma al 9 automáticamente.
        patternWeights: { "1": -2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque D del 1: «qué la debilitaría claramente»."
      },
      {
        id: "S6_c",
        text: "I don't have a clear enough example to answer.",
        signals: [],
        evidenceTags: [],
        counterevidenceTags: [],
        // Dato ausente.
        patternWeights: SIN_SENAL,
        confidence: "low",
        sourceStatus: "inferencia",
        notes: "Salida sin señal. Es la mitigación del problema ipsativo (decisión " +
               "0.3): sin ella, la elección forzada obliga a puntuar algo siempre.\n" +
               "TEXTO CORREGIDO en la calibración, mismo motivo que S5_d: «I can't " +
               "think of a decision I made quickly» podía leerse como indecisión del " +
               "6 o poca autoobservación del 9. «No tengo un ejemplo claro» es dato " +
               "ausente y nada más."
      }
    ]
  },

  // ── S7 · contraevidencia contra 9 y contra 1 ────────────────────────────
  {
    id: "S7",
    prompt: "When there's real pressure on you, what happens to what you'd decided?",
    context: "pressure from other people or from a deadline",
    purpose: "Second counterevidence route, and the audience test for pattern 1.",
    required: true,
    version: V,
    options: [
      {
        id: "S7_a",
        text: "It holds. I've stuck to unpopular decisions more than once.",
        signals: ["decisiones firmes sostenidas bajo presión"],
        evidenceTags: [],
        counterevidenceTags: ["9:nombra-preferencia"],
        // Sostener una postura impopular bajo presión contradice la reducción de
        // fricción. No suma al 1 ni al 6: muchos tipos son firmes.
        patternWeights: { "9": -2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque D del 9: «qué la debilitaría claramente»."
      },
      {
        id: "S7_b",
        text: "In private I let the standard drop — it only really matters if someone will see it.",
        signals: ["el estándar depende de la audiencia"],
        evidenceTags: [],
        counterevidenceTags: ["1:estandar-independiente-de-audiencia"],
        // Si el estándar cae en privado, la conducta depende de ser visto.
        // Contraevidencia específica del 1, y posible señal del 3 — que no está en
        // el alfa.
        patternWeights: { "1": -2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque E del 1 frente al 3: «que el estándar se relaje en privado» " +
               "es contraevidencia del 1. El 3 no está en el alfa, así que aquí " +
               "sólo resta, no suma a nadie."
      },
      {
        id: "S7_c",
        text: "I go quiet and let it pass rather than hold the line.",
        signals: ["pasividad ante el conflicto"],
        evidenceTags: ["9:evita-conflicto"],
        counterevidenceTags: [],
        // Callar y dejar pasar para no sostener el conflicto: retirada.
        patternWeights: { "9": 2 },
        confidence: "medium",
        sourceStatus: "fuente",
        notes: "Bloque A del 9: «pasividad ante el conflicto»."
      }
    ]
  }
];

/**
 * Los dos desempates REALES del alfa. Sustituyen a `FAKE_DIFFERENTIATOR_1_9`,
 * que apuntaba a un par que el canon no trata.
 *
 * `maxInfluence` también lo pone el autor: es scoring. Calibrado a 2, igual
 * que el techo que el autor fijó para las situaciones: un desempate no puede
 * pesar MÁS que una situación, o dejaría de romper el empate para crearlo.
 * Y ahora el motor lo aplica de verdad — antes el campo no lo leía nadie.
 */
export const DRAFT_DIFFERENTIATORS: DifferentiationQuestion[] = [
  {
    id: "D_1_6",
    candidatePair: ["1", "6"],
    prompt: "When you catch a mistake of your own, what's the first thing that goes through your head?",
    discriminatesBy: "corrección interna (1) frente a consecuencia y riesgo (6)",
    maxInfluence: 2,
    sourceRefs: ["Plantilla_maestra_nueve_eneatipos.docx, Eneatipo 1, Bloque E, frente a tipo 6"],
    version: V,
    options: [
      {
        id: "D_1_6_a",
        text: "That I should have caught it — I know better than that.",
        signals: ["autocrítica por estándar propio"],
        evidenceTags: ["1:esta-bien-hecho"],
        counterevidenceTags: [],
        // «I should have caught it»: reproche del crítico interno, transgresión del
        // estándar personal.
        patternWeights: { "1": 2 },
        confidence: "high",
        sourceStatus: "fuente"
      },
      {
        id: "D_1_6_b",
        text: "What it might cause now, and who else it lands on.",
        signals: ["anticipación de consecuencia"],
        evidenceTags: ["6:es-seguro"],
        counterevidenceTags: [],
        // Primero las consecuencias y a quién le caen: riesgo anticipado, no condena
        // moral del error.
        patternWeights: { "6": 2 },
        confidence: "high",
        sourceStatus: "fuente"
      }
    ]
  },
  {
    id: "D_9_6",
    candidatePair: ["9", "6"],
    prompt: "You've been asked for your position and you haven't given one. What's closer to the truth?",
    discriminatesBy: "evitar la tensión (9) frente a no tener aún la certeza (6)",
    maxInfluence: 2,
    sourceRefs: ["Plantilla_maestra_nueve_eneatipos.docx, Eneatipo 9, Bloque E, frente a tipo 6"],
    version: V,
    options: [
      {
        id: "D_9_6_a",
        text: "I'd rather not put something between us over this.",
        signals: ["evitar la tensión de tomar partido"],
        evidenceTags: ["9:da-igual"],
        counterevidenceTags: [],
        // No declarar la posición para no meter distancia: conservar el vínculo.
        patternWeights: { "9": 2 },
        confidence: "high",
        sourceStatus: "fuente"
      },
      {
        id: "D_9_6_b",
        text: "I don't know enough yet to be sure I'd be right.",
        signals: ["necesidad de certeza antes de posicionarse"],
        evidenceTags: ["6:busca-certeza"],
        counterevidenceTags: [],
        // Se retiene por falta de certeza. Lo difícil no es el vínculo, es fiarse
        // del propio juicio.
        patternWeights: { "6": 2 },
        confidence: "high",
        sourceStatus: "fuente"
      }
    ]
  }
];
